import { ProductTypeString } from '@epicuro-next/data/baskets-dto';
import { RecommendationsPanelsConfig } from '@epicuro-next/models/recommendation-panels';
import { BasketRowsGroupType } from 'graphql-api/baskets-api-interfaces';

import { ISearchNewsConfig } from './types';
import { BasketNotificationStyle } from './types/basket-notification-style';
import {
  ProductImageMissingVisualConfig,
  UpdateTitleOptionsModel,
} from './types/product-image-missing-visual-config';

export const PULSE_FEATURE_NAME = 'pulse';

export enum PulseConfigurationKeys {
  AttributesPriorityConfig = 'attributes-priority-config',
  AccountAddressReverted = 'accountAddressReverted',
  AccountPerformancePageEnabled = 'account-performance-page-enabled',
  AccountPerformancePageSlot = 'account-performance-page-slot',
  AccountPerformanceWidgetEngineEnabled = 'account-performance-widget-engine-enabled',
  AccountPerformanceSellIn = 'account-performance-sell-in',
  AccountReviewersV2Enabled = 'account-reviewers-v2-enabled',
  UseBasketsReviewV2 = 'use-baskets-review-v2',
  AccountPerformanceFeedbackEnabled = 'account-performance-feedback-enabled',
  AdvancedMDOAvailable = 'advanced-mdo-available',
  AdvancedMDOSizesAvailable = 'advanced-mdo-sizes-available',
  AllRulesFetchAtOnce = 'all-rules-fetch-at-once',
  BannerEnabled = 'banner-enabled',
  BannerLinkEnabled = 'banner-link-enabled',
  BasketNotificationStyle = 'basket-notification-style',
  ContactsUserMenu = 'contacts-user-menu',
  CustomerProductIdentifiersEnabled = 'customer-product-identifiers',
  DefaultCBasketGrouping = 'default-cbasket-grouping',
  DeliveryShowAccountId = 'delivery-show-account-id',
  DisabledImagesFormats = 'disabled-images-formats',
  DisableVideoContextMenu = 'disable-video-context-menu',
  EnableShareBrowserApi = 'enable-share-browser-api',
  discountFormat = 'discount-format',
  DocumentControlCommercialListBlacklist = 'document-control-commercial-list-blacklist',
  DocumentControlEnabled = 'document-control-enabled',
  ElementDynamicStyles = 'element-dynamic-styles',
  EnableCatalogOnlyMode = 'enable-catalog-only-mode',
  EnableUseragreementButtonsAtEOD = 'enable-useragreement-buttons-at-eod',
  EventHubTracking = 'event-hub-tracking',
  EventTrackMapping = 'event-tracking-mapping',
  GTMEcommerceBasketProductItemMapper = 'gtm-ecommerce-basket-product-item-mapper',
  GTMEcommerceCheckBasketActionFieldMapper = 'gtm-ecommerce-check-basket-action-field-mapper',
  GTMEcommerceEventsEnabled = 'gtm-ecommerce-events-enabled',
  GTMEcommerceEventsMap = 'gtm-ecommerce-events-map',
  GTMEcommercePurchaseActionFieldMapper = 'gtm-ecommerce-purchase-action-field-mapper',
  GTMEcommercePurchaseExtrasMapper = 'gtm-ecommerce-purchase-extras-mapper',
  GTMEcommerceRegularProductItemMapper = 'gtm-ecommerce-regular-product-item-mapper',
  GTMEcommerceTrackCheckBasketOnFailure = 'gtm-ecommerce-track-check-basket-on-failure',
  GTMEcommerceUseInternalIds = 'gtm-ecommerce-use-internal-ids',
  HighlightFutureTypeLinesEnabled = 'highlight-future-type-lines-enabled',
  InformationBannerAutoHeightEnabled = 'information-banner-auto-height-enabled',
  InvoicesPageEnabled = 'invoices-page-enabled',
  PowerListEnabled = 'power-list-enabled',
  PreventVisualSearchInEdge = 'prevent-visual-search-in-edge',
  PreventZoomByPixelRatio = 'prevent-zoom-by-pixel-ratio',
  ProductIdAdditionalPrefix = 'products:AdditionalPrefix',
  ProductImageMissingVisual = 'product-image-missing-visual',
  ProductTitleFormat = 'product-title',
  ProductTitleLevel = 'products:ProductTitleLevel',
  RecommendationsPanels = 'recommendations-panels',
  SearchSuggestEnabled = 'search-suggest-enabled',
  SearchNewsConfig = 'search-news-config',
  SecondaryNavigationBarMaxLinks = 'secondary-navigation-bar-max-links',
  ShowMerchandiserChatbot = 'show-merchandiser-chatbot',
  ShowMarkup = 'show-markup',
  ShowMaterialWidthIds = 'show-material-width-ids',
  ShowWholeSalePricesSettingsDefaultValue = 'show-wsp-price-setting-default-value',
  ShowAccountInHeader = 'show-account-in-header',
  AccountSelectorBackgroundImage = 'account-selector-background-image',
  ShowAccountTooltipInHeader = 'show-account-tooltip-in-header',
  SpotlightSlotReservationEnabled = 'spotlight-slot-reservation-enabled',
  SubscribeToAllEmails = 'subscribe-to-all-emails',
  ShowWelcomeModalAgainSetting = 'show-welcome-modal-again-setting',
  TermsConditionsUserconsent = 'terms-conditions-userconsent',
  TrackingHostEventHubName = 'tracking-host-event-hub-name',
  TrackingHostNamespace = 'tracking-host-namespace',
  TrueDemandEnabled = 'true-demand-enabled',
  Variations = 'showVariationsInProductBasket',
  VersionPopupTracking = 'version-popup-tracking',
  UpdateTitleOptions = 'update-title-options',
  UseHeartIconForBookmark = 'use-heart-icon-for-bookmark',
  UseSearchApiForAvailability = 'use-search-api-for-availability',
  GcrNormalizePriceForPurchaseEvent = 'gcr-normalize-price-for-purchase-event',
  RecommendationSlotsFromMerchandiser = 'recommendation-slots-from-merchandiser',
  QuickSearchDropdown = 'quick-search-dropdown',
  UserMenuItemsBadges = 'user-menu-items-badges',
  ProductLabelAttributeKeys = 'product-label-attribute-keys',
  EnabledSearchViews = 'enabled-search-views',
  EnableFiltersVisible = 'enable-filters-visible',
  ToggleDiscountLabelByWsp = 'toggle-discount-label-by-wsp',
  HideAsideMenu = 'hide-aside-menu',
  TooltipToShow = 'tooltip-to-show',
  EnableOnboardingAddon = 'enable-onboarding-addon',
  PasswordMinLength = 'password-min-length',
  UserFeedbackPages = 'user-feedback-pages',
  BulkAgreementsEnabled = 'bulk-agreements-enabled',
  NotificationPreferencesEnabled = 'notification-preferences-enabled',
  ScannerConfiguration = 'scanner-configuration',
  EnableProductSearchPdfExport = 'enable-product-search-pdf-export',
  TrackingEventsEnabled = 'tracking-events-enabled',
}

export interface IUserFeedbackPageConfig {
  delayInSec?: number;
  contextType: string;
  enabled: boolean;
}

export type UserFeedbackPagesConfig = Record<string, IUserFeedbackPageConfig>;

/**
 * Everything the PWA barcode scanner reads, as one JSON rule. Any field may be
 * omitted; the defaults below fill the gaps, so a tenant only states what it
 * wants to change.
 */
export interface IScannerConfiguration {
  /** Highlight, zoom and torch instead of the legacy bracket overlay. */
  enabled: boolean;
  /** Camera height requested when the stream is opened, px. */
  resolution: number;
  /**
   * Zoom held while hunting for a code, relative to the resting zoom of the
   * camera. Narrows the field of view so a barcode covers more pixels, which
   * is what decides whether it reads from a distance. 1 disables it.
   */
  idleZoom: number;
  /** Extra zoom once a code is found, relative to the resting zoom. */
  detectZoom: number;
  /**
   * Height the frame is squashed to before decoding. The grayscale conversion
   * runs over every canvas pixel on every attempt, so this is the main lever
   * on decode cost; horizontal resolution is untouched. 0 keeps the native
   * height.
   */
  decodeHeight: number;
  /** Pause between failed decode attempts, ms. Keeps the main thread free. */
  decodeInterval: number;
  /** Decode only the band `object-fit: cover` actually shows. */
  decodeVisibleOnly: boolean;
  /** On-device diagnostics button. Never enable for real users. */
  debug: boolean;
}

export const defaultScannerConfiguration: IScannerConfiguration = {
  enabled: false,
  resolution: 720,
  idleZoom: 1,
  detectZoom: 1.8,
  decodeHeight: 540,
  decodeInterval: 80,
  decodeVisibleOnly: true,
  debug: false,
};

export interface PulseConfigurations {
  [PulseConfigurationKeys.AttributesPriorityConfig]: string;
  [PulseConfigurationKeys.AccountAddressReverted]: boolean;
  [PulseConfigurationKeys.AccountReviewersV2Enabled]: boolean;
  [PulseConfigurationKeys.UseBasketsReviewV2]: boolean;
  [PulseConfigurationKeys.AccountPerformanceFeedbackEnabled]: boolean;
  [PulseConfigurationKeys.AccountPerformancePageEnabled]: boolean;
  [PulseConfigurationKeys.AccountPerformancePageSlot]: string;
  [PulseConfigurationKeys.AccountPerformanceWidgetEngineEnabled]: boolean;
  [PulseConfigurationKeys.AccountPerformanceSellIn]: string;
  [PulseConfigurationKeys.AdvancedMDOAvailable]: boolean;
  [PulseConfigurationKeys.AdvancedMDOSizesAvailable]: boolean;
  [PulseConfigurationKeys.AllRulesFetchAtOnce]: boolean;
  [PulseConfigurationKeys.BannerEnabled]: boolean;
  [PulseConfigurationKeys.BannerLinkEnabled]: boolean;
  [PulseConfigurationKeys.BasketNotificationStyle]: BasketNotificationStyle;
  [PulseConfigurationKeys.ContactsUserMenu]: boolean;
  [PulseConfigurationKeys.CustomerProductIdentifiersEnabled]: boolean;
  [PulseConfigurationKeys.DefaultCBasketGrouping]: BasketRowsGroupType;
  [PulseConfigurationKeys.DeliveryShowAccountId]: boolean;
  [PulseConfigurationKeys.DisabledImagesFormats]: string[];
  [PulseConfigurationKeys.DisableVideoContextMenu]: boolean;
  [PulseConfigurationKeys.EnableShareBrowserApi]: boolean;
  [PulseConfigurationKeys.discountFormat]: string;
  [PulseConfigurationKeys.DocumentControlCommercialListBlacklist]: string[];
  [PulseConfigurationKeys.DocumentControlEnabled]: boolean;
  [PulseConfigurationKeys.ElementDynamicStyles]: string;
  [PulseConfigurationKeys.EnableCatalogOnlyMode]: boolean;
  [PulseConfigurationKeys.EnableUseragreementButtonsAtEOD]: boolean;
  [PulseConfigurationKeys.EventHubTracking]: boolean;
  [PulseConfigurationKeys.EventTrackMapping]: string;
  [PulseConfigurationKeys.GTMEcommerceBasketProductItemMapper]: string;
  [PulseConfigurationKeys.GTMEcommerceCheckBasketActionFieldMapper]: string;
  [PulseConfigurationKeys.GTMEcommerceEventsEnabled]: boolean;
  [PulseConfigurationKeys.GTMEcommerceEventsMap]: string;
  [PulseConfigurationKeys.GTMEcommercePurchaseActionFieldMapper]: string;
  [PulseConfigurationKeys.GTMEcommercePurchaseExtrasMapper]: string;
  [PulseConfigurationKeys.GTMEcommerceRegularProductItemMapper]: string;
  [PulseConfigurationKeys.GTMEcommerceTrackCheckBasketOnFailure]: boolean;
  [PulseConfigurationKeys.GTMEcommerceUseInternalIds]: boolean;
  [PulseConfigurationKeys.HighlightFutureTypeLinesEnabled]: boolean;
  [PulseConfigurationKeys.InformationBannerAutoHeightEnabled]: boolean;
  [PulseConfigurationKeys.InvoicesPageEnabled]: boolean;
  [PulseConfigurationKeys.PowerListEnabled]: boolean;
  [PulseConfigurationKeys.PreventVisualSearchInEdge]: boolean;
  [PulseConfigurationKeys.PreventZoomByPixelRatio]: boolean;
  [PulseConfigurationKeys.ProductIdAdditionalPrefix]: string;
  [PulseConfigurationKeys.ProductImageMissingVisual]: string;
  [PulseConfigurationKeys.ProductTitleFormat]: string[];
  [PulseConfigurationKeys.ProductTitleLevel]: ProductTypeString;
  [PulseConfigurationKeys.RecommendationsPanels]: string;
  [PulseConfigurationKeys.SearchSuggestEnabled]: boolean;
  [PulseConfigurationKeys.SearchNewsConfig]: string;
  [PulseConfigurationKeys.SecondaryNavigationBarMaxLinks]: number;
  [PulseConfigurationKeys.ShowMerchandiserChatbot]: boolean;
  [PulseConfigurationKeys.ShowMarkup]: boolean;
  [PulseConfigurationKeys.ShowMaterialWidthIds]: boolean;
  [PulseConfigurationKeys.ShowWholeSalePricesSettingsDefaultValue]: boolean;
  [PulseConfigurationKeys.ShowAccountInHeader]: boolean;
  [PulseConfigurationKeys.AccountSelectorBackgroundImage]: string;
  [PulseConfigurationKeys.ShowAccountTooltipInHeader]: boolean;
  [PulseConfigurationKeys.SpotlightSlotReservationEnabled]: boolean;
  [PulseConfigurationKeys.SubscribeToAllEmails]: boolean;
  [PulseConfigurationKeys.ShowWelcomeModalAgainSetting]: boolean;
  [PulseConfigurationKeys.TermsConditionsUserconsent]: boolean;
  [PulseConfigurationKeys.TrackingHostEventHubName]: string;
  [PulseConfigurationKeys.TrackingHostNamespace]: string | boolean;
  [PulseConfigurationKeys.TrueDemandEnabled]: boolean;
  [PulseConfigurationKeys.Variations]: boolean;
  [PulseConfigurationKeys.VersionPopupTracking]: boolean;
  [PulseConfigurationKeys.UpdateTitleOptions]: string;
  [PulseConfigurationKeys.UseHeartIconForBookmark]: boolean;
  [PulseConfigurationKeys.UseSearchApiForAvailability]: boolean;
  [PulseConfigurationKeys.GcrNormalizePriceForPurchaseEvent]: string;
  [PulseConfigurationKeys.RecommendationSlotsFromMerchandiser]: string[];
  [PulseConfigurationKeys.QuickSearchDropdown]: boolean;
  [PulseConfigurationKeys.UserMenuItemsBadges]: string;
  [PulseConfigurationKeys.ProductLabelAttributeKeys]: string[];
  [PulseConfigurationKeys.EnabledSearchViews]: string[];
  [PulseConfigurationKeys.EnableFiltersVisible]: boolean;
  [PulseConfigurationKeys.ToggleDiscountLabelByWsp]: boolean;
  [PulseConfigurationKeys.HideAsideMenu]: boolean;
  [PulseConfigurationKeys.TooltipToShow]: string;
  [PulseConfigurationKeys.EnableOnboardingAddon]: boolean;
  [PulseConfigurationKeys.PasswordMinLength]: number;
  [PulseConfigurationKeys.UserFeedbackPages]: string;
  [PulseConfigurationKeys.BulkAgreementsEnabled]: boolean;
  [PulseConfigurationKeys.NotificationPreferencesEnabled]: boolean;
  [PulseConfigurationKeys.ScannerConfiguration]: string;
  [PulseConfigurationKeys.EnableProductSearchPdfExport]: boolean;
  [PulseConfigurationKeys.TrackingEventsEnabled]: string[];
}

export const defaultProductImageMissingVisualConfig: ProductImageMissingVisualConfig =
  {
    enabled: false,
  };

export const defaultRecommendationsPanelsConfig: RecommendationsPanelsConfig = {
  AddedToBasketNotification: [],
  AddedToBasketNotificationPane: [],
  BelowCBasket: [],
  BelowConfigStrip: [],
  Checkout: [],
  Checkout2: [],
  Spotlight: [],
  PBasket: [],
  SearchBar: [],
  SearchSuggestBar: [],
  SearchProducts: [],
  ProductBasketHeader: [],
};

export const defaultTitleOptionsConfig: UpdateTitleOptionsModel = {
  titleOptions: {
    cBasket: false,
    configStrip: false,
    search: false,
  },
};

export const defaultSearchNewsConfig: ISearchNewsConfig = {
  titles: [],
  options: {},
};

export const pulseConfigurationDefaultValues = {
  [PulseConfigurationKeys.AttributesPriorityConfig]: '{}',
  [PulseConfigurationKeys.AccountAddressReverted]: true,
  [PulseConfigurationKeys.AccountReviewersV2Enabled]: false,
  [PulseConfigurationKeys.UseBasketsReviewV2]: false,
  [PulseConfigurationKeys.AccountPerformanceFeedbackEnabled]: false,
  [PulseConfigurationKeys.AccountPerformancePageEnabled]: false,
  [PulseConfigurationKeys.AccountPerformanceWidgetEngineEnabled]: false,
  [PulseConfigurationKeys.AccountPerformanceSellIn]: '',
  [PulseConfigurationKeys.AdvancedMDOAvailable]: false,
  [PulseConfigurationKeys.AdvancedMDOSizesAvailable]: false,
  [PulseConfigurationKeys.AllRulesFetchAtOnce]: true,
  [PulseConfigurationKeys.BasketNotificationStyle]:
    BasketNotificationStyle.default,
  [PulseConfigurationKeys.ContactsUserMenu]: false,
  [PulseConfigurationKeys.DeliveryShowAccountId]: false,
  [PulseConfigurationKeys.DisableVideoContextMenu]: false,
  [PulseConfigurationKeys.EnableShareBrowserApi]: false,
  [PulseConfigurationKeys.discountFormat]: '1.0-0',
  [PulseConfigurationKeys.DocumentControlCommercialListBlacklist]: [],
  [PulseConfigurationKeys.GTMEcommerceEventsEnabled]: false,
  [PulseConfigurationKeys.ElementDynamicStyles]: '{}',
  [PulseConfigurationKeys.EnableCatalogOnlyMode]: false,
  [PulseConfigurationKeys.EnableUseragreementButtonsAtEOD]: false,
  [PulseConfigurationKeys.EventTrackMapping]: '{}',
  [PulseConfigurationKeys.HighlightFutureTypeLinesEnabled]: false,
  [PulseConfigurationKeys.InformationBannerAutoHeightEnabled]: false,
  [PulseConfigurationKeys.InvoicesPageEnabled]: false,
  [PulseConfigurationKeys.PowerListEnabled]: false,
  [PulseConfigurationKeys.PreventVisualSearchInEdge]: false,
  [PulseConfigurationKeys.PreventZoomByPixelRatio]: false,
  [PulseConfigurationKeys.ProductIdAdditionalPrefix]: '',
  [PulseConfigurationKeys.ProductImageMissingVisual]: JSON.stringify(
    defaultProductImageMissingVisualConfig,
  ),
  [PulseConfigurationKeys.RecommendationsPanels]: JSON.stringify(
    defaultRecommendationsPanelsConfig,
  ),
  [PulseConfigurationKeys.SearchSuggestEnabled]: false,
  [PulseConfigurationKeys.SearchNewsConfig]: JSON.stringify(
    defaultSearchNewsConfig,
  ),
  [PulseConfigurationKeys.ShowMerchandiserChatbot]: false,
  [PulseConfigurationKeys.ShowMarkup]: false,
  [PulseConfigurationKeys.ShowMaterialWidthIds]: false,
  [PulseConfigurationKeys.ShowWholeSalePricesSettingsDefaultValue]: true,
  [PulseConfigurationKeys.ShowAccountInHeader]: false,
  [PulseConfigurationKeys.AccountSelectorBackgroundImage]: '',
  [PulseConfigurationKeys.ShowAccountTooltipInHeader]: false,
  [PulseConfigurationKeys.SpotlightSlotReservationEnabled]: false,
  [PulseConfigurationKeys.SubscribeToAllEmails]: false,
  [PulseConfigurationKeys.ShowWelcomeModalAgainSetting]: false,
  [PulseConfigurationKeys.TermsConditionsUserconsent]: false,
  [PulseConfigurationKeys.TrueDemandEnabled]: false,
  [PulseConfigurationKeys.VersionPopupTracking]: false,
  [PulseConfigurationKeys.UpdateTitleOptions]: JSON.stringify(
    defaultTitleOptionsConfig,
  ),
  [PulseConfigurationKeys.UseHeartIconForBookmark]: true,
  [PulseConfigurationKeys.UseSearchApiForAvailability]: false,
  [PulseConfigurationKeys.GcrNormalizePriceForPurchaseEvent]: '',
  [PulseConfigurationKeys.RecommendationSlotsFromMerchandiser]: [],
  [PulseConfigurationKeys.QuickSearchDropdown]: false,
  [PulseConfigurationKeys.UserMenuItemsBadges]: '{}',
  [PulseConfigurationKeys.ProductLabelAttributeKeys]: [],
  [PulseConfigurationKeys.EnabledSearchViews]: [],
  [PulseConfigurationKeys.EnableFiltersVisible]: false,
  [PulseConfigurationKeys.ToggleDiscountLabelByWsp]: false,
  [PulseConfigurationKeys.HideAsideMenu]: false,
  [PulseConfigurationKeys.TooltipToShow]: '[]',
  [PulseConfigurationKeys.EnableOnboardingAddon]: false,
  [PulseConfigurationKeys.PasswordMinLength]: 12,
  [PulseConfigurationKeys.UserFeedbackPages]: '{}',
  [PulseConfigurationKeys.NotificationPreferencesEnabled]: false,
  [PulseConfigurationKeys.ScannerConfiguration]: '{}',
  [PulseConfigurationKeys.EnableProductSearchPdfExport]: false,
  [PulseConfigurationKeys.TrackingEventsEnabled]: [],
};
